"use client";

import {
  NavigationMenu,
  NavigationMenuLink,
  NavigationMenuList,
} from "../ui/navigation-menu";

import ContactDialog from "../ContactDialog";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { forwardRef } from "react";
import { useDataContext } from "../../app/_context";
import { usePathname } from "next/navigation";

// Structure for each navigation item
interface NavItem {
  _key: string;
  title: string;
  link: string;
}

const ListItem = forwardRef<
  React.ElementRef<"a">,
  React.ComponentPropsWithoutRef<"a">
>(({ className, title, children, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
            className,
          )}
          {...props}
        >
          <div className="text-sm font-medium leading-none">{title}</div>
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
            {children}
          </p>
        </a>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = "ListItem";

export default function NavigationMainMenu({
  navItems,
}: {
  navItems: NavItem[];
}) {
  const { navbarFixed, currentProjectInfo } = useDataContext();
  const pathname = usePathname();

  if (!pathname.includes("public")) {
    return (
      <div className="flex w-full justify-center mx-auto">
        <NavigationMenu
          className={cn(
            "hover:bg-transparent hover:dark:bg-transparent",
            "hidden lg:flex rounded-full transition-colors",
            !navbarFixed && "bg-stone-100 dark:bg-[#1014195e]",
          )}
        >
          <NavigationMenuList>
            {navItems.map((item) => {
              const linkClass =
                "group inline-flex h-9 w-max place-items-center rounded-full px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:bg-stone-100 hover:dark:bg-[#1014195e] focus:outline-none disabled:pointer-events-none disabled:opacity-50";
              if (item.link === "/contact") {
                return (
                  <ContactDialog key={item._key}>
                    <button className={linkClass}>{item.title}</button>
                  </ContactDialog>
                );
              }
              return (
                <NavigationMenuLink asChild key={item._key}>
                  <Link href={item.link} className={linkClass} prefetch={false}>
                    {item.title}
                  </Link>
                </NavigationMenuLink>
              );
            })}
          </NavigationMenuList>
        </NavigationMenu>
      </div>
    );
  } else {
    return (
      <div className="md:flex w-full hidden justify-center mx-auto text-sm md:text-base">
        <p className="text-center truncate px-4">{currentProjectInfo.title}</p>
      </div>
    );
  }
}
